import { AfterViewInit, Component, DestroyRef, ElementRef, HostBinding, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, filter, firstValueFrom, interval, map, Observable, startWith, Subscription, switchMap, take, tap, timer, } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import { MenuSelectors } from '@states/menu/menu.selector-types';
import { MainMenuItem } from '../main-menu.service';
import { ActiveOrganization, UserOrganizationDropDown } from '@models/organization.model';
import * as OrganizationSelectors from '@states/organization/organization.selectors';
import { NavigationError, NavigationStart, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthenticationService } from '../../authentication/authentication.service';
import { routerSegments } from '@consts/routes';
import { api } from '@consts/url.const';
import { ConfirmDialogModel, RouteModalService, UiButtonComponent } from '@ui';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { SupportAccessModalComponent } from '@modals/support-access-modal/support-access-modal.component';
import { AboutComponent } from '../../settings/about/about.component';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { UserSettings, UserSettingsModels } from '@models/user-settings';
import * as UserSettingsSelectors from '@states/user-settings/user-settings.selectors';
import * as uuid from 'uuid';
import { ConfirmDialogService } from '../../shared/confirm-dialog/confirm-dialog.service';
import { environment } from '../../../environments/environment';
import { UtilsV2Service } from '../../services/utils-v2.service';
import packageInfo from '@pkg';
import { UpdateFailedDialogComponent } from '../../shared/update-failed-dialog/update-failed-dialog.component';
import { AppsMenuComponent, PartnerAppSelection, UiConfirmDialogComponent } from '@ui';
import { ServiceWorkerHandlerService } from 'src/app/services/service-worker-handler.service';
import { AppUpdateService, SW_UPDATE_BUST_PARAM, SW_UPDATE_VERSION_PARAM } from '../../services/app-update.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PermissionModel } from '@models/permission.model';
import { PermissionSelectors } from '@states/permissions/permissions.selector-types';
import { UserSelectors } from '@states/user/user.selector-types';
import { UserState } from '@states/user/user.reducer';
import { parseJwt } from '../../helpers/common.helpers';
import { AuthenticationSelectors } from '@states/authentication/authentication.selector-types';
import { AuthenticationActions } from '@states/authentication/authentication.action-types';
import { LetDirective } from '@ngrx/component';
import { MatTooltip } from '@angular/material/tooltip';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { AuthorizationModule } from '../../authorization/authorization.module';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { UiKitModule } from '../../shared/ui-kit/ui-kit.module';
import { UiDropdownComponent } from '@ui';
import { SwitchOrganizationComponent } from './components/switch-organization/switch-organization.component';
import { RbacOrgAllowDirective } from '../../authorization/rbac-org-allow.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserSettingsActions } from '@states/user-settings/user-settings.action-types';
import { UserSettingsEffect } from '@effects/user-settings.effect';
import { ofType } from '@ngrx/effects';
import { OrganizationEffect } from '@effects/organization.effect';
import { OrganizationsActions } from '@states/organization/organization.actions-types';
import { CustomDirectionality } from '../../services/directionality.service';
import { IntegrationsModel } from '@models/integrations.model';
import { IntegrationsSelectors } from '@states/integrations/integrations.selector-types';
import { IntegrationsActions } from '@states/integrations/integrations.action-types';
import { ControlEventsSelectors } from '@states/control-events/control-events.selector-types';
import { ControlEventsActions } from '@states/control-events/control-events.action-types';
import { LocalStorageService, UiAvatarComponent } from '@ui';
import { LocalStorageKeys } from '@consts/shared.const';
import { IfOperatorDirective } from '../../directives/if-operator.directive';
import { UiTruncatedHtmlComponent } from '../../shared/ui-kit/ui-truncated-html/ui-truncated-html.component';
import { IfMagentrixDirective } from '../../directives/if-magentrix.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IfDeveloperDirective } from '../../directives/if-developer.directive';
import { UserEffects } from '@effects/user.effects';
import { FeedbackType, G2FeedbackModalComponent, } from '@modals/g2-feedback-modal/g2-feedback-modal.component';
import { SocketBootstrapService } from '../../services/socket-bootstrap.service';
import { UserService } from '../../user/user.service';
import { selectIntegrations } from '@states/integrations/integrations.selectors';
import { chatRoomsFeature } from '@share-chat';
import LOCATION_DEVICE_COLLAPSE_STATE = LocalStorageKeys.LOCATION_DEVICE_COLLAPSE_STATE;
import LOCATION_DEVICE_SCROLL_TOP = LocalStorageKeys.LOCATION_DEVICE_SCROLL_TOP;

const NGSW_INTERVAL = 1800000;

const alertsPages = {
  monitoring: 'monitoring',
  monitoringV3: 'monitoring-v3',
  create: 'create',
  configuration: 'configuration',
  groups: 'groups',
  zones: 'zones',
  zoneMonitoring: 'zoneMonitoring',
  callCenter: 'callCenter'
};

type AlertsPageTarget = (typeof alertsPages)[keyof typeof alertsPages];

const CHAT_SIDEBAR_UNREAD_BADGE_CAP = 99;

@Component({
  selector: 'app-sidebar-v2',
  templateUrl: './sidebar-v2.component.html',
  styleUrls: ['./sidebar-v2.component.scss'],
  standalone: true,
  imports: [
    LetDirective,
    MatTooltip,
    NgForOf,
    AuthorizationModule,
    RouterLink,
    RouterLinkActive,
    MatRippleModule,
    MatIconModule,
    NgIf,
    UiKitModule,
    ReactiveFormsModule,
    UiDropdownComponent,
    RouterOutlet,
    SwitchOrganizationComponent,
    AsyncPipe,
    RbacOrgAllowDirective,
    TranslatePipe,
    IfOperatorDirective,
    UiButtonComponent,
    UiAvatarComponent,
    UiTruncatedHtmlComponent,
    IfMagentrixDirective,
    IfDeveloperDirective,
    AppsMenuComponent,
    RbacOrgAllowDirective,
  ],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarV2Component implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('uiSearchComponent') uiSearchComponent: HTMLElement;
  @ViewChildren(MatTooltip) tooltips: QueryList<MatTooltip>;

  private serviceWorkerSubscription$: Subscription;
  private newVersionSubscription$: Subscription;

  public IntegrationProviders = IntegrationsModel.Providers;

  private reloadId = null;
  private appVersion = null;
  public selectLevel1Menu$: Observable<MainMenuItem[]> = this.store$.pipe(
    select(MenuSelectors.selectLevel1Menu),
  );
  public selectSelectedUserSettings$: Observable<UserSettings> = this.store$.pipe(
    select(UserSettingsSelectors.selectSelectedUserSettings),
  );

  public selectEnableControlCenter$: Observable<boolean> = this.store$.pipe(select(OrganizationSelectors.selectEnableControlCenter));
  public selectIsControlCenterAllowed$: Observable<boolean> = combineLatest([
    this.selectEnableControlCenter$,
    this.store$.pipe(
      select(PermissionSelectors.checkAccessPermissionsOR([PermissionModel.Permissions.ControlCenterManagement], [])),
    ),
  ]).pipe(map(([controlCenterEnabled, hasControlCenterManagementPermission]) => controlCenterEnabled && hasControlCenterManagementPermission));
  public selectEnableWorkflows$: Observable<boolean> = this.store$.pipe(select(OrganizationSelectors.selectEnableWorkflows));
  public selectEnablePanic$: Observable<boolean> = this.store$.pipe(select(OrganizationSelectors.selectEnablePanic));
  public selectIsPanicAllowed$: Observable<boolean> = combineLatest([
    this.selectEnablePanic$,
    this.store$.pipe(
      select(PermissionSelectors.checkAccessPermissionsOR([PermissionModel.Permissions.PanicAppManagement], [])),
    ),
  ]).pipe(map(([panicEnabled, hasPanicAppManagementPermission]) => panicEnabled && hasPanicAppManagementPermission));
  public selectEnableSidebarVideoTooltips$: Observable<boolean> = this.store$.pipe(
    select(OrganizationSelectors.selectEnableSidebarVideoTooltips),
  );
  public selectHideSupportButtons$: Observable<boolean> = this.store$.pipe(
    select(OrganizationSelectors.selectHideSupportButtons),
  );
  public selectCblEnabled$: Observable<boolean> = this.store$.pipe(select(OrganizationSelectors.selectCblEnabled));
  public selectUseWallV3Page$: Observable<boolean> = this.store$.pipe(
    select(OrganizationSelectors.selectUseWallV3Page),
  );
  public selectMatrixChatActive$: Observable<boolean> = this.store$.pipe(
    select(OrganizationSelectors.selectIsOrganizationMatrixChatActive),
  );

  public selectG2Url$: Observable<string> = this.store$.pipe(select(UserSettingsSelectors.selectG2Url));

  public selectIdToken$: Observable<string> = this.store$.pipe(select(AuthenticationSelectors.idToken));
  private idToken: string;

  public uuid = uuid.v4();

  swUpdateAvailable = false;

  public isDevTeam$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isDevTeam));

  public selectIntegrations$: Observable<IntegrationsModel.Providers[]> = this.store$.pipe(select(IntegrationsSelectors.selectIntegrations));

  public isDeveloper$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isDeveloper));
  public selectActiveOrganization$: Observable<ActiveOrganization> = this.store$.pipe(
    select(OrganizationSelectors.selectActiveOrganization),
  );

  public isPartner$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isPartner));

  public selectUserOrganizationsDropdown$: Observable<UserOrganizationDropDown[]> = this.store$.pipe(
    select(OrganizationSelectors.selectUserOrganizationsDropdown),
  );

  public selectUser$: Observable<UserState> = this.store$.pipe(select(UserSelectors.selectUser));

  public isOrgSelectorOpen: boolean = false;

  public routerSegments = routerSegments;

  public readonly chatSidebarUnreadTotal = this.store$.selectSignal(
    chatRoomsFeature.selectChatSidebarUnreadTotal,
  );

  public readonly matrixChatSidebarUnreadBadgeAllowed = this.store$.selectSignal(
    OrganizationSelectors.selectIsOrganizationMatrixChatSynapseConfigured,
  );

  public selectUseAlertsV3PageSig = this.store$.selectSignal(OrganizationSelectors.selectUseAlertsV3Page);

  public darkThemeFormControl: UntypedFormControl = new UntypedFormControl(false);
  public langControl: UntypedFormControl = new UntypedFormControl(true);
  @HostBinding('class') className = '';
  public darkClassName = 'dark-theme';
  public lightClassName = 'light-theme';
  public permissions = PermissionModel.Permissions;
  public swUpdateTrial = false;

  public integrations$: Observable<IntegrationsModel.Providers[]> = this.store$.select(
    IntegrationsSelectors.selectIntegrations,
  );
  private hasControlEventsData$: Observable<boolean> = this.store$.select(
    ControlEventsSelectors.selectHasControlEventsData,
  );
  private isControlEventsDistinctLoaded$: Observable<boolean> = this.store$.select(
    ControlEventsSelectors.selectIsDistinctLoaded,
  );
  private testDataSubscription;
  @ViewChild('userMenu') userMenu!: ElementRef;
  userMenuHeight: number = 0;

  @ViewChild('orgMenu') orgMenu!: ElementRef;
  orgMenuHeight: number = 0;

  @ViewChild('supportMenu') supportMenu!: ElementRef;
  supportMenuHeight: number = 0;

  @ViewChild('appsMenu')
  public appsMenu!: ElementRef<HTMLElement>
  public appsMenuHeight = 0;

  public mainMenuItems: MainMenuItem[] = [];
  public origMainMenuItems: MainMenuItem[] = [];

  public managementMenuItem: MainMenuItem = {
    displayName: 'MAIN_MENU.MANAGEMENT_PORTAL',
    path: routerSegments.managementPortal,
    roles: ['user'],
    disabled: false,
    class: 'ui-icon-dashboards-sidebar',
    permissions: [PermissionModel.Permissions.PortalManagement],
  };

  public partnerMenuItem: MainMenuItem = {
    displayName: 'MAIN_MENU.PARTNER_PORTAL',
    path: routerSegments.partnerPortal,
    roles: ['user'],
    disabled: false,
    class: 'ui-icon-dashboards-sidebar',
    permissions: [PermissionModel.Permissions.PortalManagement],
  };

  public readonly appId = environment.appId;
  public readonly webUrl = environment.apps.webUrl;
  public readonly controlCenterUrl = environment.apps.controlCenterUrl;
  public readonly parkingUrl = environment.apps.parkingUrl;
  public readonly sitePlannerUrl = environment.apps.sitePlannerUrl;
  public readonly panicUrl = environment.apps.panicUrl;


  constructor(
    private store$: Store<AppState>,
    public router: Router,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    private confirmService: ConfirmDialogService,
    private utilsV2Service: UtilsV2Service,
    private serviceWorkerHandlerService: ServiceWorkerHandlerService,
    private socketBootstrapService: SocketBootstrapService,
    private userSettingsEffects: UserSettingsEffect,
    private organizationEffects: OrganizationEffect,
    public directionality: CustomDirectionality,
    private localStorageService: LocalStorageService,
    private ngZone: NgZone,
    private destroyRef: DestroyRef,
    public userEffects$: UserEffects,
    private http: HttpClient,
    private userService: UserService,
    private translate: TranslateService,
    private appUpdateService: AppUpdateService,
    private modalService: RouteModalService,
  ) {}

  public getMainMenuItemTooltip(item: MainMenuItem): string {
    if (
      item.path === routerSegments.chat
      && this.matrixChatSidebarUnreadBadgeAllowed()
      && this.chatSidebarUnreadTotal() > 0
    ) {
      return this.translate.instant('CHAT.SIDEBAR_MENU_TOOLTIP', {
        count: this.chatSidebarUnreadBadgeText(),
      });
    }
    return this.translate.instant(item.displayName);
  }

  public chatSidebarUnreadBadgeText(): string {
    const n = this.chatSidebarUnreadTotal();
    if (n <= 0) {
      return '';
    }
    return n > CHAT_SIDEBAR_UNREAD_BADGE_CAP
      ? `${CHAT_SIDEBAR_UNREAD_BADGE_CAP}+`
      : String(n);
  }

  public chatSidebarUnreadBadgeAriaLabel(): string {
    if (!this.matrixChatSidebarUnreadBadgeAllowed() || this.chatSidebarUnreadTotal() <= 0) {
      return '';
    }
    return this.translate.instant('CHAT.SIDEBAR_UNREAD_BADGE_ARIA', {
      count: this.chatSidebarUnreadBadgeText(),
    });
  }

  public get isHelpSection() {
    return this.router.url.includes(routerSegments.help);
  }

  public get activeState() {
    return (
      this.router.url.includes(routerSegments.cameraView) ||
      this.router.url.includes(routerSegments.cameraEdit)
    );
  }

  async ngOnInit() {
    this.store$.dispatch(IntegrationsActions.getIntegrations());
    this.store$.dispatch(ControlEventsActions.getDistinct());

    this.selectLevel1Menu$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((menuItems) => {
      this.origMainMenuItems = menuItems;
    });

    combineLatest([
      this.selectLevel1Menu$,
      this.integrations$,
      this.hasControlEventsData$,
      this.isControlEventsDistinctLoaded$,
      this.selectEnableWorkflows$,
      this.selectMatrixChatActive$,
      this.selectCblEnabled$,
      this.selectUseWallV3Page$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([
          menuItems,
          integrations,
          hasData,
          isLoaded,
          workflowsEnabled,
          matrixChatActive,
          cblEnabled,
          useWallV3Page,
        ]) => {
        let computed = menuItems;
        if (!integrations?.includes(IntegrationsModel.Providers.Lumana)) {
          computed = computed.filter((item) => item.path !== routerSegments.alarmManagement);
        }
        const hasAccessControlIntegration =
          integrations?.includes(IntegrationsModel.Providers.Genea) ||
          integrations?.includes(IntegrationsModel.Providers.Kisi) ||
          integrations?.includes(IntegrationsModel.Providers.Brivo) ||
          integrations?.includes(IntegrationsModel.Providers.Schneider) ||
          integrations?.includes(IntegrationsModel.Providers.AccessControlBridge);
        const shouldShowControlEvents = (isLoaded && hasData) || hasAccessControlIntegration;
        if (!shouldShowControlEvents) {
          computed = computed.filter((item) => item.path !== routerSegments.accessControlEvents);
        }

        if (!workflowsEnabled) {
          computed = computed.filter((item) => item.path !== routerSegments.workflows);
        }

        if (!matrixChatActive) {
          computed = computed.filter((item) => item.path !== routerSegments.chat);
        }

        if (!cblEnabled) {
          computed = computed.filter((item) => item.path !== routerSegments.advancedAnalyticsJobs);
        }

        computed = computed.map((item) => {
          if (item.path !== routerSegments.wallV2 && item.path !== routerSegments.wallV3) {
            return item;
          }
          return {
            ...item,
            path: useWallV3Page ? routerSegments.wallV3 : routerSegments.wallV2,
          };
        });

        this.mainMenuItems = computed;
      });
    this.userSettingsEffects.updateUserLanguage$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(UserSettingsActions.updateUserLanguageSuccess),
        tap(() => {
          window.location.reload();
        }),
      )
      .subscribe();
    this.socketBootstrapService.init();
    this.selectIdToken$.subscribe((res) => {
      this.idToken = res;
    });

    // this.swUpdateNotificationService.resetManuallyClosed()
    // this.swUpdateNotificationService.changeNotification('Your custom message');

    // this.testDataSubscription = this.swUpdateNotificationService.startEmittingTestData().subscribe(
    //   data => {
    //     this.swUpdateNotificationService.changeNotification(JSON.stringify(data));
    //   }
    // );

    this.setupServiceWorkerListener();
    await this.subscribeToSwUpdate();
    this.listenForChunkLoadErrors();

    this.selectSelectedUserSettings$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (res.lang) {
        this.langControl.setValue(res.lang === UserSettingsModels.LANGUAGE.ENGLISH, { emitEvent: false });
      }
      if (res.theme === 'dark') {
        this.darkThemeFormControl.setValue(true, { emitEvent: false });
        document.body.classList.add(this.darkClassName);
        document.body.classList.remove(this.lightClassName);
      } else {
        this.darkThemeFormControl.setValue(false, { emitEvent: false });
        document.body.classList.add(this.lightClassName);
        document.body.classList.remove(this.darkClassName);
      }
    });

    //For debug only
    this.darkThemeFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.store$.dispatch(UserSettingsActions.updateUserTheme({ theme: res ? 'dark' : 'light' }));
    });

    this.langControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.store$.dispatch(
        UserSettingsActions.updateUserLanguage({
          lang: res ? UserSettingsModels.LANGUAGE.ENGLISH : UserSettingsModels.LANGUAGE.HEBREW,
        }),
      );
    });

    this.organizationEffects.updateOrganizationServer$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(OrganizationsActions.updateOrganizationLanguage),
        tap(() => {
          window.location.reload();
        }),
      )
      .subscribe();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.hideTooltips();
      });

    this.selectG2Url$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((x) => !!x),
        switchMap((url) =>
          timer(2000).pipe(
            switchMap(() =>
              this.dialog
                .open(G2FeedbackModalComponent, {
                  width: '565px',
                  data: { url },
                })
                .afterClosed(),
            ),
          ),
        ),
        tap((result) => {
          if (result === FeedbackType.REMIND_LATER) {
            this.store$.dispatch(UserSettingsActions.setG2RemindLater());
          } else if (result === FeedbackType.DONT_ASK_AGAIN) {
            this.store$.dispatch(UserSettingsActions.setG2DontAskAgain());
          }
        }),
      )
      .subscribe();

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.openSupportAccess,
          open: () => this.openSupportAccessModal(),
        },
      ],
      this.destroyRef,
    );
  }

  ngAfterViewInit() {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => this.measure());
  }

  get isFullscreen() {
    return this.utilsV2Service.isFullscreen();
  }

  private measure() {
    this.userMenuHeight = this.userMenu.nativeElement.clientHeight;
    this.orgMenuHeight = this.orgMenu.nativeElement.clientHeight;
    this.supportMenuHeight = this.supportMenu.nativeElement.clientHeight;
    this.appsMenuHeight = this.appsMenu?.nativeElement?.clientHeight;
  }

  private async updateApp() {
    // Exit full screen and present confirmation dialog
    if (this.isFullscreen) {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document['mozCancelFullScreen']) {
        await document['mozCancelFullScreen']();
      } else if (document['webkitCancelFullScreen']) {
        await document['webkitCancelFullScreen']();
      }
    }

    if (this.swUpdateTrial) {
      this.swUpdateFailed();
    } else {
      this.confirmUpdate();
    }
  }

  private async extractAppVersion() {
    try {
      if (
        !(
          environment.env === 'production' ||
          environment.env === 'dev-hosted' ||
          environment.env === 'staging-hosted'
        )
      ) {
        console.warn('skiping version extranction in non hosted environment');
        return {};
      }

      const response = await fetch(`/ngsw.json?ngsw-cache-bust=${Date.now()}`);
      const data = await response.json();

      const version = packageInfo.version;
      this.appVersion = data?.appData?.version;

      console.log(`packageInfo version: ${version}, incoming version: ${this.appVersion}`);

      return {
        appVersion: data?.appData?.version,
        currentVersion: version,
      };
    } catch (error) {
      console.error('error fetching ngsw.json', error);
      return {};
    }
  }

  // login() {
  //   // this.auth0Service.selfHostedlogin("edan.sorski@lumix.ai", "Lumix12345!").subscribe()
  //   this.auth0Service.socialLogin('google-oauth2').subscribe()
  // }

  private listenForChunkLoadErrors(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationError => event instanceof NavigationError),
        filter((event) => this.appUpdateService.isChunkLoadError(event.error)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        console.warn('Stale chunk detected via navigation error — prompting update.');
        this.swUpdateAvailable = true;
        this.updateApp();
      });

    this.appUpdateService.chunkLoadError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        console.warn('Stale chunk detected via global error — prompting update.');
        this.swUpdateAvailable = true;
        this.updateApp();
      });
  }

  private async checkForUpdatesDirectly() {
    const response = await this.extractAppVersion();
    if (response.appVersion !== response.currentVersion) {
      this.updateApp();
    }
  }

  private checkForUpdatesFallback() {
    interval(NGSW_INTERVAL)
      .pipe(
        startWith(0),
        tap(() => {
          this.checkForUpdatesDirectly();
        }),
      )
      .subscribe();
  }

  public async subscribeToSwUpdate() {
    const url = new URL(location.href);

    const urlSwUpdateBust = url.searchParams.get(SW_UPDATE_BUST_PARAM);
    const urlSwVersion = url.searchParams.get(SW_UPDATE_VERSION_PARAM);

    if (!!urlSwUpdateBust && !!urlSwVersion) {
      const response = await this.extractAppVersion();

      if (response.currentVersion !== response.appVersion || response.currentVersion !== urlSwVersion) {
        const comparison = this.compareVersions(response.currentVersion, urlSwVersion);

        if (this.validSwVersionHash(urlSwVersion)) {
          if (comparison < 0) {
            this.swUpdateTrial = true;
            this.updateApp();
            return;
          }
        }

        this.swUpdateTrial = true;
        this.updateApp();
        return;
      }

      this.appUpdateService.clearPostUpdateNavigationState(url);
      console.log('App successfully updated to latest version.');
    }

    this.checkForUpdatesFallback();

    // if (this.swUpdate.isEnabled) {
    //   this.swUpdate.versionUpdates
    //     .subscribe({
    //       next: async (event: any) => {
    //         console.log('version information:', event);
    //         switch (event.type) {
    //           case 'VERSION_DETECTED':
    //             console.log(`new version detected - downloading new app version: ${event.version.hash}`);
    //             break;
    //           case 'VERSION_READY':
    //             this.checkForUpdatesDirectly();
    //             console.log(`Current app version: ${event.currentVersion.hash}`);
    //             console.log(`New app version ready for use: ${event.latestVersion.hash}`);
    //             break;

    //           case 'NO_NEW_VERSION_DETECTED':
    //             console.log(`no new version detected - checking for new app version`);
    //             const newVersion = await this.swUpdate.checkForUpdate();
    //             if (!!newVersion) {
    //               this.checkForUpdatesDirectly();
    //             }
    //             break;

    //           case 'VERSION_INSTALLATION_FAILED':
    //             console.log(`failed to install app version '${event.version.hash}': ${event.error}`);
    //             this.checkForUpdatesDirectly();
    //             break;
    //         }
    //       },
    //       error: (error: any) => {
    //         console.log(`error getting app version`);
    //         this.checkForUpdatesFallback();
    //       },
    //       complete: () => { }
    //     });
    // }
  }

  public identify(index, item) {
    return index;
  }

  public logout(): void {
    this.store$.dispatch(AuthenticationActions.Logout({ params: {} }));
  }

  public about(isDeveloper?: boolean, dropdown?: UiDropdownComponent): void {
    this.hideTooltips();
    dropdown?.closeDropdown();

    window.open('https://support.lumana.ai/hc/en-us/requests', '_blank');
    return;
    // if (isDeveloper) {
    //   this.router.navigate([routerSegments.help]);
    //   return;
    // }
    this.dialog.open(AboutComponent, {
      width: '410px',
      height: '410px',
    });
  }

  reloadPage(tenete = false): void {
    console.log('reload has been triggered');

    if (tenete) {
      void this.appUpdateService.prepareForHardReload().finally(() => {
        window.location.reload();
      });
      return;
    }

    void this.appUpdateService.prepareForHardReload().finally(() => {
      this.appUpdateService.hardReload(this.appVersion?.toString());
    });
  }

  swUpdateFailed() {
    if (!!this.isDialogOpen) {
      return;
    }
    this.dialog
      .open(UpdateFailedDialogComponent, {
        width: '435px',
        panelClass: 'modal-no-padding',
      })
      .afterClosed()
      .subscribe(() => {
        this.swUpdateAvailable = true;
      });
  }

  public get isDialogOpen() {
    return this.dialog.openDialogs.some(
      (dialogRef) =>
        dialogRef.componentInstance instanceof UiConfirmDialogComponent ||
        dialogRef.componentInstance instanceof UpdateFailedDialogComponent,
    );
  }

  confirmUpdate() {
    if (!!this.isDialogOpen) {
      return;
    }
    this.confirmService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'Lumana app update required',
        msg: 'A new version is available. Please update now in order to continue.',
        confirm: 'Update',
        hideCancel: true,
        disableClose: true,
      })
      .subscribe((res) => {
        this.authenticationService
          .onSwUpdate()
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.activateWorker();
            },
            error: () => {
              this.activateWorker();
            },
            complete: () => {
              this.activateWorker();
            },
          });
      });
  }

  async setupServiceWorkerListener() {
    this.serviceWorkerSubscription$ = this.serviceWorkerHandlerService.message$.subscribe(async (event) => {
      if (
        (event.data && event.data.type === 'SKIP_WAITING_CONFIRMED') ||
        (event.data && event.data.type === 'RELOAD_TABS')
      ) {
        const reloadId = event?.data?.body?.reloadId;

        if ((this.reloadId === null || this.reloadId === undefined) && reloadId !== this.reloadId) {
          this.reloadPage();
        }
      }
      if (event.data && event.data.type === 'SWITCH_TENANT_CONFIRMED') {
        this.localStorageService.removeItem(LOCATION_DEVICE_COLLAPSE_STATE);
        this.localStorageService.removeItem(LOCATION_DEVICE_SCROLL_TOP);
        try {
          const orgId = event?.data?.body?.orgId;

          const encodedToken = parseJwt(this.idToken) ?? null;

          if (orgId !== encodedToken?.orgId) {
            setTimeout(() => {
              window.location.href = '/';
            }, 1000);
          }
        } catch (e) {
          console.error('another tab failed to switch organization');
        }
      }
      if (event.data && event.data.type === 'LOGOUT_CONFIRMED') {
        if (event?.data?.body?.tabId === this.serviceWorkerHandlerService.tabId) {
          return;
        }
        this.reloadPage(true);
      }
    });
  }

  activateWorker(): void {
    this.reloadId = Math.floor(Math.random() * 1001);
    this.serviceWorkerHandlerService.sendMessage('SKIP_WAITING', { reloadId: this.reloadId }, false);
    this.reloadPage();
  }

  validSwVersionHash(swVersion: any) {
    const versionRegex = /^(\d+)\.(\d+)\.(\d+)$/;

    return !!swVersion && versionRegex.test(swVersion);
  }

  compareVersions(version1, version2) {
    const v1 = version1?.split('.').map(Number);
    const v2 = version2?.split('.').map(Number);

    for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
      const num1 = v1[i] || 0;
      const num2 = v2[i] || 0;

      if (num1 !== num2) {
        return num1 - num2;
      }
    }

    return 0;
  }

  ngOnDestroy(): void {
    if (!!this.serviceWorkerSubscription$) {
      this.serviceWorkerSubscription$.unsubscribe();
    }

    if (!!this.newVersionSubscription$) {
      this.newVersionSubscription$.unsubscribe();
    }

    if (this.testDataSubscription) {
      this.testDataSubscription.unsubscribe();
    }
  }

  public toggleSwitchOrgMenu() {
    this.isOrgSelectorOpen = !this.isOrgSelectorOpen;
    if (this.isOrgSelectorOpen) {
      this.hideTooltips();
    }
  }

  public closeSwitchOrgMenu() {
    this.isOrgSelectorOpen = false;
    this.hideTooltips();
  }

  private hideTooltips(): void {
    if (this.tooltips?.length) {
      this.tooltips.forEach((tooltip) => tooltip.hide(0));
    }
  }

  public magentrixLogin(): void {
    window.open('https://partners.lumana.ai', '_blank');
    return;
  }

  public openZendeskChat(): void {
    // Check if Zendesk widget is already loaded
    if (typeof window['zE'] !== 'undefined') {
      window['zE']('messenger', 'open');
    } else {
      // Load Zendesk widget script if not already loaded
      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = 'https://static.zdassets.com/ekr/snippet.js?key=25aaebb3-4f60-4662-a8fd-eb31ff70868e';
      script.onload = () => {
        // Open the widget after it's loaded
        if (typeof window['zE'] !== 'undefined') {
          window['zE']('messenger', 'open');
        }
      };
      document.head.appendChild(script);
    }
  }

  public async openHelpCenter(dropdown?: UiDropdownComponent): Promise<void> {
    this.hideTooltips();
    dropdown?.closeDropdown();
    try {
      const response = await firstValueFrom(
        this.http.get<{ url: string }>(api.support.gitbookAuthUrl),
      );
      window.open('https://' + response.url, '_blank');
    } catch (error) {
      console.error('Failed to open Help Center', error);
    }
  }

  public filter(menuItems: MainMenuItem) {}

  public navigateAlertsMenu(
    target: AlertsPageTarget,
    dropdown: UiDropdownComponent,
  ): void {
    dropdown.toggleDropdown();

    switch (target) {
      case alertsPages.monitoring:
        this.router.navigate(['/', routerSegments.alertsV2, routerSegments.monitoring]);
        return;
      case alertsPages.monitoringV3:
        this.router.navigate(['/', routerSegments.alertsV2, routerSegments.monitoringV3]);
        return;
      case alertsPages.create:
        this.router.navigate(['/', routerSegments.alertsV2, routerSegments.templates]);
        return;
      case alertsPages.configuration:
        this.router.navigate(['/', routerSegments.alertsV2, routerSegments.list]);
        return;
      case alertsPages.groups:
        this.router.navigate(['/', routerSegments.alertsV2, routerSegments.alarms, routerSegments.list]);
        return;
      case alertsPages.zones:
        this.router.navigate(['/', routerSegments.monitoring, routerSegments.accountsList]);
        return;
      case alertsPages.zoneMonitoring:
        this.router.navigate(['/', routerSegments.monitoring, routerSegments.accountAlertsMonitoring]);
        return;
      case alertsPages.callCenter:
        this.router.navigate(['/', routerSegments.alarmManagement]);
        return;
    }
  }

  public switchApp({ url }: PartnerAppSelection): void {
    const redirectUrl = new URL(url, window.location.origin).origin;
    const loginUrl = new URL(api.authV2.login);
    loginUrl.searchParams.set('redirectUrl', redirectUrl);
    window.open(loginUrl.toString(), '_blank', 'noopener,noreferrer');
  }

  protected readonly alertsPages = alertsPages;

  public openSupportAccess(dropdown: UiDropdownComponent): void {
    dropdown.toggleDropdown();
    this.modalService.openModal(openDialogByParams.openSupportAccess);
  }

  public openSupportEmail(event: MouseEvent, dropdown: UiDropdownComponent): void {
    this.hideTooltips();
    dropdown.closeDropdown();

    window.location.href = 'mailto:support@lumana.ai';
  }

  private openSupportAccessModal(): MatDialogRef<SupportAccessModalComponent> {
    return this.dialog.open(SupportAccessModalComponent, {
      width: '600px',
      height: '360px',
      disableClose: false,
    });
  }
}
